import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddCrewMemberInput, ConfirmCertificateInput, DocType } from "@gb/contracts";
import { api, errorMessage } from "../../api/client";

export const crewKeys = {
  roster: (tenantId: string, projectId: string) =>
    ["tenants", tenantId, "projects", projectId, "crew"] as const,
  person: (tenantId: string, personId: string | null) =>
    ["tenants", tenantId, "crew", personId] as const,
};

export function useRoster(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: crewKeys.roster(tenantId, projectId),
    retry: false,
    queryFn: async () => {
      const res = await api.crew.roster({ params: { tenantId, projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function usePersonCertificates(tenantId: string, personId: string | null) {
  return useQuery({
    queryKey: crewKeys.person(tenantId, personId),
    enabled: personId !== null,
    queryFn: async () => {
      const res = await api.crew.certificates({
        params: { tenantId, personId: personId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.certificates;
    },
  });
}

export function useAddCrewMember(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddCrewMemberInput) => {
      const res = await api.crew.addMember({ params: { tenantId, projectId }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.person;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(tenantId, projectId) }),
  });
}

export function useOffboard(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (personId: string) => {
      const res = await api.crew.offboard({ params: { tenantId, projectId, personId }, body: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.person;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(tenantId, projectId) }),
  });
}

export function useReonboard(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (personId: string) => {
      const res = await api.crew.reonboard({ params: { tenantId, projectId, personId }, body: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.person;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(tenantId, projectId) }),
  });
}

/**
 * The certificate chain: presign, plain PUT of the bytes to storage (no
 * bearer header — it is S3), then complete, which reads the card and answers
 * with a suggestion for the confirm form. Nothing counts towards compliance
 * until confirmed.
 */
export function useUploadCertificate(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      personId,
      type,
      file,
    }: {
      personId: string;
      type: DocType;
      file: File;
    }) => {
      const presigned = await api.crew.certUploadUrl({
        params: { tenantId, personId },
        body: {
          type,
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        },
      });
      if (presigned.status !== 201) throw new Error(errorMessage(presigned));

      const put = await fetch(presigned.body.uploadUrl, {
        method: "PUT",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`The upload didn't finish (${put.status}).`);

      const completed = await api.crew.completeCertUpload({
        params: { tenantId, personId, docId: presigned.body.documentId },
        body: {},
      });
      if (completed.status !== 200) throw new Error(errorMessage(completed));
      return completed.body;
    },
    onSuccess: (_data, { personId }) =>
      void queryClient.invalidateQueries({ queryKey: crewKeys.person(tenantId, personId) }),
  });
}

export function useConfirmCertificate(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      personId,
      docId,
      input,
    }: {
      personId: string;
      docId: string;
      input: ConfirmCertificateInput;
    }) => {
      const res = await api.crew.confirmCertificate({
        params: { tenantId, personId, docId },
        body: input,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.certificate;
    },
    onSuccess: (_data, { personId }) => {
      void queryClient.invalidateQueries({ queryKey: crewKeys.person(tenantId, personId) });
      // Compliance state derives from confirmed certificates.
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(tenantId, projectId) });
    },
  });
}

export function useDeleteCertificate(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ personId, docId }: { personId: string; docId: string }) => {
      const res = await api.crew.deleteCertificate({ params: { tenantId, personId, docId } });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: (_data, { personId }) => {
      void queryClient.invalidateQueries({ queryKey: crewKeys.person(tenantId, personId) });
      void queryClient.invalidateQueries({ queryKey: crewKeys.roster(tenantId, projectId) });
    },
  });
}
